/**
 * Planificador anual del presupuesto (puro): una fila por categoría (ingresos, gastos fijos, gastos variables
 * y el objetivo de ahorro), con su importe mensual, los meses distintos, la media real de los últimos 12 meses
 * y lo real de cada mes. El pie calcula ingresos − gastos − objetivo = "sin asignar".
 */
import { monthRange, prevMonth, round2, SAVINGS_GOAL } from '@pf/domain';
import type { Computed } from './model.js';
import { appliesTo, plannedBudgetAdd } from './planned.js';

export type Section = 'income' | 'fixed' | 'variable' | 'goal';

export interface PlanRow {
  category: string;
  section: Section;
  monthly: number | null;
  overrides: Array<number | null>;
  warn: boolean;
  note: string;
  /** Media mensual real de los últimos 12 meses cerrados con datos (en positivo). */
  avg12: number | null;
  /** Real de cada mes del año (en positivo); null en meses futuros. */
  real: Array<number | null>;
  /** Tenía fila en Presupuesto (para saber si guardar o quitar). */
  existed: boolean;
  /** Gastos previstos de cada mes: lo que suman al presupuesto («Suma») y sus conceptos (también los ya incluidos). */
  adds?: number[];
  plannedLabels?: Array<Array<{ concept: string; amount: number; included: boolean }>>;
}

/** Presupuesto de un mes: lo escrito (o el mensual) más lo que suman los gastos previstos. */
export const planned = (r: Pick<PlanRow, 'monthly' | 'overrides' | 'adds'>, i: number) => (r.overrides[i] ?? r.monthly ?? 0) + (r.adds?.[i] ?? 0);
export const plannedYear = (r: Pick<PlanRow, 'monthly' | 'overrides' | 'adds'>) => round2(Array.from({ length: 12 }, (_, i) => planned(r, i)).reduce((t, v) => t + v, 0));

export function plannerRows(c: Computed, year: number, todayMonth: string): PlanRow[] {
  const real = (category: string, month: string, sign: 1 | -1) =>
    round2(sign * c.items.filter(i => i.month === month && i.category === category).reduce((t, i) => t + i.attributable, 0));
  // Los 12 meses cerrados antes del actual (p. ej. en octubre de 2026: de octubre de 2025 a septiembre de 2026)
  const last12 = monthRange(`${Number(todayMonth.slice(0, 4)) - 1}${todayMonth.slice(4)}`, prevMonth(todayMonth));
  // Si el libro empieza más tarde, la media se hace solo con los meses que tienen datos
  const first = c.months[c.months.length - 1] ?? todayMonth;
  const window = last12.filter(m => m >= first);
  const months = Array.from({ length: 12 }, (_, i) => `${year}-${String(i + 1).padStart(2, '0')}`);
  const row = (category: string, section: Section): PlanRow => {
    const b = c.wb.budget.find(x => x.year === year && x.category.toLowerCase() === category.toLowerCase());
    const sign = section === 'income' ? 1 : -1;
    const hist = section === 'goal' ? [] : window.map(m => real(category, m, sign));
    const any = hist.some(v => v !== 0);
    return {
      category, section, monthly: b ? b.monthly : null, overrides: b ? [...b.overrides] : Array(12).fill(null), warn: !!b?.warn, note: b?.note ?? '',
      avg12: any ? round2(hist.reduce((t, v) => t + v, 0) / hist.length) : null,
      real: months.map(m => (section === 'goal' || m > todayMonth ? null : real(category, m, sign))),
      existed: !!b,
      adds: Array.from({ length: 12 }, (_, i) => plannedBudgetAdd(c.wb.planned ?? [], c.wb.budget, year, i, category)),
      plannedLabels: Array.from({ length: 12 }, (_, i) => (c.wb.planned ?? []).filter(p => p.month - 1 === i && p.category.toLowerCase() === category.toLowerCase() && appliesTo(p, year))
        .map(p => ({ concept: p.concept, amount: p.amount, included: p.budget === 'included' || !b }))),
    };
  };
  const cats = c.wb.categories;
  return [
    ...cats.filter(x => x.kind === 'income').map(x => row(x.name, 'income')),
    ...cats.filter(x => x.kind === 'expense' && x.nature === 'fixed').map(x => row(x.name, 'fixed')),
    ...cats.filter(x => x.kind === 'expense' && x.nature !== 'fixed').map(x => row(x.name, 'variable')),
    row(SAVINGS_GOAL, 'goal'),
  ];
}

export interface PlanTotals { income: number[]; expense: number[]; goal: number[]; unassigned: number[]; year: { income: number; expense: number; goal: number; unassigned: number } }

export function planTotals(rows: PlanRow[]): PlanTotals {
  const sum = (section: Section[], i: number) => round2(rows.filter(r => section.includes(r.section)).reduce((t, r) => t + planned(r, i), 0));
  const income = Array.from({ length: 12 }, (_, i) => sum(['income'], i));
  const expense = Array.from({ length: 12 }, (_, i) => sum(['fixed', 'variable'], i));
  const goal = Array.from({ length: 12 }, (_, i) => sum(['goal'], i));
  const unassigned = income.map((v, i) => round2(v - expense[i]! - goal[i]!));
  const total = (a: number[]) => round2(a.reduce((t, v) => t + v, 0));
  return { income, expense, goal, unassigned, year: { income: total(income), expense: total(expense), goal: total(goal), unassigned: total(unassigned) } };
}
